import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Gear, Shield, Key, Plus, Trash, Database, Toggle, Warning, Image as ImageIcon } from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import DangerZoneModal from "../../components/DangerZoneModal";

// Iter 209 — `data-reset` tab removed from Settings per superadmin
// request. The bulk-wipe surface was retired to protect tenant data;
// individual "Delete Selected" flows remain in Customers / Invoices.
const TABS = ["tenant", "branding", "invoice-pdf", "features", "security", "api-keys", "roles", "backup"];

export default function SystemSettings() {
  const [tab, setTab] = useState("tenant");
  const { user } = useAuth();
  const visibleTabs = TABS;
  return (
    <AppShell title="System Settings" subtitle="Module 15">
      <div className="flex gap-1 mb-6 border-b border-[#E5E2D9] overflow-x-auto">
        {visibleTabs.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`sys-tab-${t}`}
                  className={`px-5 py-2.5 text-sm capitalize whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-[#2D5A27] text-[#1A1A1A] font-medium" : "border-transparent text-[#5C5A55]"}`}>
            {t.replace("-", " ")}
          </button>
        ))}
      </div>
      {tab === "tenant" && <TenantTab/>}
      {tab === "branding" && <BrandingTab/>}
      {tab === "invoice-pdf" && <InvoicePdfTab/>}
      {tab === "features" && <FeaturesTab/>}
      {tab === "security" && <SecurityTab/>}
      {tab === "api-keys" && <ApiKeysTab/>}
      {tab === "roles" && <RolesTab/>}
      {tab === "backup" && <BackupTab/>}
    </AppShell>
  );
}

function TenantTab() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/settings/tenant").then(r => setForm(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/settings/tenant", form); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!form) return <div className="p-6">Loading…</div>;
  return (
    <form onSubmit={save} className="max-w-xl bg-white border border-[#E5E2D9] rounded-md p-6 space-y-4" data-testid="tenant-form">
      {[
        ["name", "Tenant name"], ["display_name", "Display name"],
        ["support_email", "Support email"], ["support_phone", "Support phone"],
        ["logo_url", "Logo URL"], ["timezone", "Timezone"], ["currency", "Currency"],
        ["default_daily_hours", "Default Daily Duty Hours"],
      ].map(([k, lbl]) => (
        <div key={k}>
          <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{lbl}</label>
          <input data-testid={`tenant-${k}`} value={form[k] || ""} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
        </div>
      ))}
      <button data-testid="tenant-save" type="submit" className="w-full h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium">Save</button>
    </form>
  );
}


// ─────────────────────────────────────────────────────────────
// Iter 138 — Invoice PDF settings tab
// Configures header lines, tax identifier, brand color, footer,
// bank details etc. that appear on the generated invoice PDF.
// ─────────────────────────────────────────────────────────────
function InvoicePdfTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/finance/invoice-pdf-settings")
      .then(r => setCfg(r.data))
      .catch(e => toast.error(formatApiError(e)));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/finance/invoice-pdf-settings", cfg);
      toast.success("Invoice PDF settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const previewPdf = (paper) => {
    // Fire a demo PDF for the most recent invoice so the user can eyeball
    // the changes without leaving the settings page. Uses `finance/invoices?limit=1`.
    api.get("/finance/invoices?limit=1")
      .then(r => {
        const first = (r.data || [])[0];
        if (!first) { toast.error("Save at least one invoice first to preview"); return; }
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/finance/invoices/${first.id}/pdf?paper=${paper}`;
        window.open(url, "_blank", "noopener");
      })
      .catch(e => toast.error(formatApiError(e)));
  };
  if (!cfg) return <div className="text-sm text-[#5C5A55]">Loading…</div>;
  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  return (
    <div className="max-w-3xl space-y-6" data-testid="invoice-pdf-tab">
      <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-semibold">
            Header
          </div>
          <div className="text-[11px] text-[#8A8780] mt-0.5">
            Shown at the top of every invoice PDF. Line 1 is the big brand name; line 2 is a small tagline.
          </div>
        </div>
        <Field label="Header line 1 (brand name)">
          <input value={cfg.header_line1 || ""} onChange={e => set("header_line1", e.target.value)}
                 data-testid="ip-header1" placeholder="e.g. Rescigo Health Pvt Ltd"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
        </Field>
        <Field label="Header line 2 (tagline)">
          <input value={cfg.header_line2 || ""} onChange={e => set("header_line2", e.target.value)}
                 data-testid="ip-header2" placeholder="Waste management & sustainability services"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
        </Field>
        <Field label="Tax identifier line (GSTIN, PAN…)">
          <input value={cfg.tax_line || ""} onChange={e => set("tax_line", e.target.value)}
                 data-testid="ip-tax" placeholder="GSTIN: 32ABCDE1234F1Z5"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Registered address">
            <input value={cfg.address_line || ""} onChange={e => set("address_line", e.target.value)}
                   data-testid="ip-addr" placeholder="Kochi, Kerala 682001"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
          </Field>
          <Field label="Contact line (footer)">
            <input value={cfg.contact_line || ""} onChange={e => set("contact_line", e.target.value)}
                   data-testid="ip-contact" placeholder="+91 12345 67890 · billing@rescigo.in"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
          </Field>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-semibold">
            Footer & branding
          </div>
        </div>
        <Field label="Footer text (thank-you / policy note)">
          <textarea value={cfg.footer_text || ""} onChange={e => set("footer_text", e.target.value)}
                    data-testid="ip-footer" rows={2}
                    placeholder="Thank you for choosing us."
                    className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md resize-y"/>
        </Field>
        <Field label="Signature label">
          <input value={cfg.signature_text || ""} onChange={e => set("signature_text", e.target.value)}
                 data-testid="ip-sig" placeholder="Authorised Signatory"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
        </Field>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-[#1A1A1A]">
            <input type="checkbox" checked={!!cfg.show_qr}
                   onChange={e => set("show_qr", e.target.checked)}
                   data-testid="ip-qr"
                   className="accent-[#2D5A27]"/>
            Show verify-QR on invoice
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-[#1A1A1A]">
            <input type="checkbox" checked={!!cfg.show_bank_details}
                   onChange={e => set("show_bank_details", e.target.checked)}
                   data-testid="ip-bank-toggle"
                   className="accent-[#2D5A27]"/>
            Show bank details on invoice
          </label>
        </div>
        {cfg.show_bank_details && (
          <Field label="Bank details">
            <textarea value={cfg.bank_details || ""} onChange={e => set("bank_details", e.target.value)}
                      data-testid="ip-bank" rows={3}
                      placeholder="Account: 12345678&#10;IFSC: HDFC0000123&#10;Bank: HDFC · Kochi"
                      className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md resize-y font-mono text-[13px]"/>
          </Field>
        )}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-semibold">
          Preview
        </div>
        <div className="text-[11px] text-[#8A8780]">
          Save first, then preview the layout with the latest saved invoice.
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => previewPdf("a4")}
                  data-testid="ip-preview-a4"
                  className="h-9 px-4 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">A4 preview</button>
          <button onClick={() => previewPdf("a5")}
                  data-testid="ip-preview-a5"
                  className="h-9 px-4 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">A5 preview</button>
          <button onClick={() => previewPdf("thermal")}
                  data-testid="ip-preview-thermal"
                  className="h-9 px-4 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">Thermal 80mm preview</button>
        </div>
      </div>

      <div>
        <button onClick={save} disabled={busy}
                data-testid="ip-save"
                className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
          {busy ? "Saving…" : "Save PDF settings"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.16em] text-[#5C5A55] block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}


function FeaturesTab() {
  const [flags, setFlags] = useState(null);
  useEffect(() => { api.get("/settings/features").then(r => setFlags(r.data)); }, []);
  const toggle = async (key) => {
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    try { await api.put("/settings/features", { features: { [key]: !flags[key] } }); toast.success(`${key} ${!flags[key] ? "enabled" : "disabled"}`); }
    catch (e) { toast.error(formatApiError(e)); setFlags(flags); }
  };
  if (!flags) return <div className="p-6">Loading…</div>;
  return (
    <div className="max-w-2xl bg-white border border-[#E5E2D9] rounded-md p-6 space-y-2" data-testid="features-list">
      {Object.entries(flags).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between p-3 border border-[#E5E2D9] rounded-md">
          <div>
            <div className="font-medium capitalize">{k.replace("_", " ")}</div>
            <div className="text-xs text-[#8A8780]">{v ? "Enabled" : "Disabled"}</div>
          </div>
          <button onClick={() => toggle(k)} data-testid={`feat-${k}`}
                  className={`w-11 h-6 rounded-full relative transition-colors ${v ? "bg-[#2D5A27]" : "bg-[#E5E2D9]"}`}>
            <span className={`absolute top-0.5 ${v ? "right-0.5" : "left-0.5"} w-5 h-5 bg-white rounded-full transition-all`}/>
          </button>
        </div>
      ))}
    </div>
  );
}

function SecurityTab() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/settings/security").then(r => setForm(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/settings/security", { ...form,
      password_min_length: +form.password_min_length, session_hours: +form.session_hours,
      max_failed_attempts: +form.max_failed_attempts, lockout_minutes: +form.lockout_minutes });
      toast.success("Saved"); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!form) return <div className="p-6">Loading…</div>;
  return (
    <form onSubmit={save} className="max-w-xl bg-white border border-[#E5E2D9] rounded-md p-6 space-y-4" data-testid="security-form">
      {[
        ["password_min_length", "Min password length"],
        ["session_hours", "Session duration (hours)"],
        ["max_failed_attempts", "Max failed login attempts"],
        ["lockout_minutes", "Lockout duration (minutes)"],
      ].map(([k, lbl]) => (
        <div key={k}>
          <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{lbl}</label>
          <input data-testid={`sec-${k}`} type="number" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
        </div>
      ))}
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={form.enforce_device_binding}
               onChange={e => setForm(f => ({ ...f, enforce_device_binding: e.target.checked }))}
               className="accent-[#2D5A27]"/>
        <span className="text-sm">Enforce one-device-per-staff</span>
      </label>
      <button type="submit" className="w-full h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium">Save</button>
    </form>
  );
}

function ApiKeysTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ provider: "twilio", key_name: "api_key", value: "" });
  const load = async () => { const r = await api.get("/settings/api-keys"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/settings/api-keys", form); toast.success("Saved"); setForm({ ...form, value: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { await api.delete(`/settings/api-keys/${id}`); load(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2"><Key size={16} color="#2D5A27" weight="fill"/><div className="font-heading">Stored API keys</div></div>
        <table className="w-full text-sm" data-testid="api-keys-table">
          <thead><tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <th className="px-6 py-3">Provider</th><th className="px-6 py-3">Key name</th><th className="px-6 py-3">Value</th><th className="px-6 py-3 text-right"></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-[#8A8780]">No keys stored.</td></tr>}
            {items.map(k => (
              <tr key={k.id}>
                <td className="px-6 py-3 font-medium">{k.provider}</td>
                <td className="px-6 py-3 font-mono text-xs">{k.key_name}</td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{k.masked}</td>
                <td className="px-6 py-3 text-right"><button onClick={() => del(k.id)} className="text-[#C25D38] text-xs inline-flex items-center gap-1"><Trash size={12}/>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={submit} className="bg-white border border-[#E5E2D9] rounded-md p-6 space-y-3" data-testid="api-key-form">
        <div className="font-heading text-lg mb-2">Add key</div>
        <select value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
          {["twilio", "google_maps", "razorpay", "phonepe", "zoho", "whatsapp", "sendgrid"].map(p => <option key={p}>{p}</option>)}
        </select>
        <input data-testid="key-name" required value={form.key_name} onChange={e => setForm(f => ({ ...f, key_name: e.target.value }))}
               placeholder="Key name (e.g. api_key, secret)" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
        <input data-testid="key-value" required value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
               placeholder="Value" type="password" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
        <button data-testid="key-save" type="submit" className="w-full h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2"><Plus size={12}/>Store</button>
        <div className="text-xs text-[#8A8780]">Keys are stored per tenant. Only admins can view the masked list.</div>
      </form>
    </div>
  );
}

function RolesTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/settings/roles").then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-6">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
        <div className="font-heading text-lg mb-4">Roles</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {data.roles.map(r => (
            <div key={r.key} className="border border-[#E5E2D9] rounded-md p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">{r.key}</div>
              <div className="font-heading text-lg">{r.label}</div>
              <div className="text-xs text-[#5C5A55] mt-1">{r.description}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] font-heading">Permission matrix</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <th className="px-6 py-3">Module</th><th className="px-6 py-3">View</th><th className="px-6 py-3">Edit</th><th className="px-6 py-3">Delete</th>
          </tr></thead>
          <tbody>
            {data.permissions.map(p => (
              <tr key={p.module}>
                <td className="px-6 py-3 font-medium">{p.module}</td>
                <td className="px-6 py-3 text-xs text-[#5C5A55]">{p.view.join(", ")}</td>
                <td className="px-6 py-3 text-xs text-[#5C5A55]">{p.edit.join(", ") || "—"}</td>
                <td className="px-6 py-3 text-xs text-[#5C5A55]">{p.delete.join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BackupTab() {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get("/settings/backup/snapshots"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const snap = async () => {
    try { await api.post("/settings/backup/snapshot"); toast.success("Snapshot created"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md">
      <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
        <Database size={16} color="#2D5A27" weight="fill"/><div className="font-heading">Backup snapshots</div><div className="flex-1"/>
        <button onClick={snap} data-testid="btn-snapshot" className="h-9 px-4 bg-[#2D5A27] text-white rounded-md text-sm">Create snapshot</button>
      </div>
      <table className="w-full text-sm" data-testid="snapshots-table">
        <thead><tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
          <th className="px-6 py-3">Date</th><th className="px-6 py-3">Total records</th><th className="px-6 py-3">Collections</th>
        </tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-[#8A8780]">No snapshots yet.</td></tr>}
          {items.map(s => (
            <tr key={s.id}>
              <td className="px-6 py-3 font-mono text-xs">{new Date(s.created_at).toLocaleString()}</td>
              <td className="px-6 py-3 font-mono font-medium">{s.total_records.toLocaleString()}</td>
              <td className="px-6 py-3 text-xs text-[#5C5A55]">{Object.keys(s.counts).length} collections</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SCOPE_LABELS = {
  customers: "Customers (+ rewards, referrals, partners)",
  invoices: "Invoices (+ transactions, payouts, payment links)",
  service_requests: "Service Requests",
  tasks: "Tasks (+ route jobs)",
  qr_bags: "QR Bags",
  complaints: "Complaints / Tickets",
  staff_sessions: "Staff Duty Sessions & Attendance",
  wards: "Wards (+ ward → zone mappings)",
  health_zones: "Health Zones (+ ward maps + collection schedules)",
  audit: "Audit Logs",
  otps: "OTPs",
  pickup_requests: "Pickup Requests",
  alerts: "Fraud / System Alerts",
  certificates: "Certificates",
};

function DataResetTab() {
  const [scopes, setScopes] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/data-reset/scopes");
      setScopes(r.data.scopes || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const togglePick = (s) => {
    const next = new Set(picked);
    next.has(s) ? next.delete(s) : next.add(s);
    setPicked(next);
  };
  const pickAll = () => setPicked(new Set(scopes.map(s => s.scope)));
  const pickNone = () => setPicked(new Set());

  const totalPicked = scopes
    .filter(s => picked.has(s.scope))
    .reduce((sum, s) => sum + (s.current_count || 0), 0);

  // Iter 178 — 2-step danger-zone gate: submit now opens a modal
  // that collects the caller's password → server emails a 6-digit
  // OTP → caller enters it → server verifies and only THEN runs
  // the wipe. Same flow will be reused for other destructive ops.
  const [dzOpen, setDzOpen] = useState(false);

  const submit = async () => {
    if (picked.size === 0) { toast.error("Pick at least one scope"); return; }
    if (confirm !== "RESET") { toast.error("Type RESET to confirm"); return; }
    setDzOpen(true);
  };

  const executeReset = async ({ challenge_id, otp }) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/data-reset", {
        scopes: Array.from(picked), confirm: "RESET",
        challenge_id, otp,
      });
      setLastResult(r.data);
      toast.success(`Reset complete — ${Object.values(r.data.deleted || {}).filter(v => typeof v === 'number').reduce((a, b) => a + b, 0)} records deleted`);
      setConfirm("");
      setPicked(new Set());
      setDzOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e; // let modal decide whether to close
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="data-reset-panel">
      {/* Hero warning */}
      <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-md p-5">
        <div className="flex items-start gap-3">
          <Warning size={24} color="#B91C1C" weight="fill" className="shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-heading text-[#7F1D1D] text-base mb-1">Destructive action — superadmin only</div>
            <div className="text-sm text-[#7F1D1D] leading-relaxed">
              Wipes selected operational data for the current tenant. Auth users, tenants, franchises, settings,
              states/districts/LSGDs are <span className="font-semibold">always preserved</span>. Use this during pre-deployment
              QA to reset between test runs. <span className="font-semibold">This option will be removed before final production deploy.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scope picker */}
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-3">
          <Trash size={16} color="#B91C1C" weight="fill"/>
          <div className="font-heading">Choose data to wipe</div>
          <div className="flex-1"/>
          <button onClick={pickAll} data-testid="data-reset-pick-all"
                  className="text-xs px-3 py-1.5 border border-[#E5E2D9] rounded hover:bg-[#F7F6F2]">
            Select all
          </button>
          <button onClick={pickNone} data-testid="data-reset-pick-none"
                  className="text-xs px-3 py-1.5 border border-[#E5E2D9] rounded hover:bg-[#F7F6F2]">
            Clear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E5E2D9]">
          <div className="divide-y divide-[#E5E2D9]">
            {scopes.slice(0, Math.ceil(scopes.length / 2)).map(s => (
              <ScopeRow key={s.scope} scope={s} checked={picked.has(s.scope)} onToggle={() => togglePick(s.scope)}/>
            ))}
          </div>
          <div className="divide-y divide-[#E5E2D9]">
            {scopes.slice(Math.ceil(scopes.length / 2)).map(s => (
              <ScopeRow key={s.scope} scope={s} checked={picked.has(s.scope)} onToggle={() => togglePick(s.scope)}/>
            ))}
          </div>
        </div>
      </div>

      {/* Confirm bar */}
      <div className="bg-white border-2 border-[#FCA5A5] rounded-md p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-[#1A1A1A] font-medium mb-1">
              {picked.size === 0
                ? "No scopes selected"
                : <>About to delete <span className="text-[#B91C1C] font-semibold">{totalPicked.toLocaleString()}</span> record(s) across <span className="font-semibold">{picked.size}</span> scope(s).</>}
            </div>
            <div className="text-xs text-[#5C5A55]">Type <span className="font-mono font-semibold text-[#B91C1C]">RESET</span> to enable the button.</div>
          </div>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type RESET"
            data-testid="data-reset-confirm-input"
            className="h-10 w-full md:w-44 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono uppercase"
          />
          <button
            onClick={submit}
            disabled={busy || picked.size === 0 || confirm !== "RESET"}
            data-testid="data-reset-submit-btn"
            className="h-10 px-6 bg-[#B91C1C] text-white rounded-md text-sm font-medium hover:bg-[#991B1B] disabled:bg-[#E5E2D9] disabled:text-[#8A8780] disabled:cursor-not-allowed transition-colors">
            {busy ? "Resetting…" : "Wipe selected data"}
          </button>
        </div>
      </div>

      {/* Last result */}
      {lastResult && (
        <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="data-reset-result">
          <div className="font-heading text-sm mb-3">Last reset summary</div>
          <div className="text-xs text-[#5C5A55] mb-3">Scopes: {lastResult.scopes.join(", ")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
            {Object.entries(lastResult.deleted || {}).map(([col, n]) => (
              <div key={col} className="flex justify-between gap-3 px-3 py-1.5 bg-[#F7F6F2] rounded">
                <span className="text-[#5C5A55] truncate">{col}</span>
                <span className="font-semibold text-[#1A1A1A]">{typeof n === "number" ? n.toLocaleString() : n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {dzOpen && (
        <DangerZoneModal
          action="data_reset"
          summary={`Wipe ${totalPicked.toLocaleString()} records across ${picked.size} scope(s)`}
          busy={busy}
          onConfirm={executeReset}
          onCancel={() => setDzOpen(false)}
        />
      )}
    </div>
  );
}

function ScopeRow({ scope, checked, onToggle }) {
  return (
    <label className={`flex items-start gap-3 px-6 py-3 cursor-pointer transition-colors ${checked ? "bg-[#FEF2F2]" : "hover:bg-[#F7F6F2]"}`}
           data-testid={`data-reset-scope-${scope.scope}`}>
      <input
        type="checkbox" checked={checked} onChange={onToggle}
        className="mt-1 accent-[#B91C1C]"
        data-testid={`data-reset-checkbox-${scope.scope}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1A1A1A]">{SCOPE_LABELS[scope.scope] || scope.scope}</div>
        <div className="text-xs text-[#8A8780] truncate">{scope.collections.join(" · ")}</div>
      </div>
      <div className="text-sm font-mono font-semibold text-[#B91C1C] shrink-0">{(scope.current_count || 0).toLocaleString()}</div>
    </label>
  );
}


function BrandingTab() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { setB((await api.get("/branding")).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  if (!b) return <div className="text-xs text-[#8A8780]">Loading…</div>;
  const upd = (k) => (e) => setB((s) => ({ ...s, [k]: e.target.value }));
  const updSocial = (k) => (e) => setB((s) => ({ ...s, social: { ...(s.social || {}), [k]: e.target.value } }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/branding", b); toast.success("Branding saved · refresh public site to verify"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-6" data-testid="branding-tab">
      <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon size={16} color="#2D5A27" weight="fill" />
          <div className="font-heading">Logos · Favicon</div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <LogoSlot label="Public-site logo (color)" url={b.logo_url} onChange={upd("logo_url")} bg="#FFF" testid="brand-logo-url" />
          <LogoSlot label="Dark-bg logo (white)" url={b.logo_white_url} onChange={upd("logo_white_url")} bg="#0A0F0D" testid="brand-logo-white-url" />
          <LogoSlot label="Favicon (32x32 PNG)" url={b.favicon_url} onChange={upd("favicon_url")} bg="#F7F6F2" small testid="brand-favicon-url" />
        </div>
        <div className="text-[11px] text-[#8A8780] mt-4">
          Tip: paste a public URL (e.g. /brand/your-logo.png after dropping into <code>frontend/public/brand/</code>, or any CDN URL like Cloudinary/S3).
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4">
        <div className="font-heading mb-2">Hero & section overrides</div>
        <Input label="Homepage hero image URL (override default)" v={b.hero_image_url || ""} onChange={upd("hero_image_url")} placeholder="https://images.unsplash.com/..." testid="brand-hero-url" />
        <Input label="Admin entry hero image URL (left panel)" v={b.admin_hero_image_url || ""} onChange={upd("admin_hero_image_url")} placeholder="Optional" testid="brand-admin-hero-url" />
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4">
        <div className="font-heading mb-2">Marketing copy</div>
        <Input label="Tagline" v={b.tagline || ""} onChange={upd("tagline")} testid="brand-tagline" />
        <Input label="Footer text" v={b.footer_text || ""} onChange={upd("footer_text")} testid="brand-footer" />
      </div>

      {/* ----------- Homepage sections ----------- */}
      <Group title="Homepage · Flow (5 steps)">
        <Input label="Section title (default: 'Five steps. Fully traceable.')" v={b.flow_title || ""} onChange={upd("flow_title")} testid="brand-flow-title" />
      </Group>

      <Group title="Homepage · Smart Service Flow">
        <Input label="Section title" v={b.section_a_title || ""} onChange={upd("section_a_title")} testid="brand-section-a-title" />
        <Textarea label="Section body" v={b.section_a_body || ""} onChange={upd("section_a_body")} testid="brand-section-a-body" />
      </Group>

      <Group title="Homepage · Franchise">
        <Input label="Title" v={b.section_c_title || ""} onChange={upd("section_c_title")} testid="brand-section-c-title" />
        <Textarea label="Body" v={b.section_c_body || ""} onChange={upd("section_c_body")} testid="brand-section-c-body" />
        <Input label="CTA button label" v={b.section_c_cta || ""} onChange={upd("section_c_cta")} testid="brand-section-c-cta" />
        <ListEditor label="Bullets (one per line)" value={b.section_c_bullets || []} onChange={(v) => setB((s) => ({ ...s, section_c_bullets: v }))} testid="brand-section-c-bullets" />
      </Group>

      <Group title="Homepage · Government strip">
        <Input label="Title" v={b.section_d_title || ""} onChange={upd("section_d_title")} testid="brand-section-d-title" />
        <Textarea label="Body" v={b.section_d_body || ""} onChange={upd("section_d_body")} testid="brand-section-d-body" />
      </Group>

      <Group title="Homepage · Sustainability impact">
        <Input label="Title" v={b.section_e_title || ""} onChange={upd("section_e_title")} testid="brand-section-e-title" />
        <Textarea label="Body" v={b.section_e_body || ""} onChange={upd("section_e_body")} testid="brand-section-e-body" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input label="KPI · kg diverted" v={b.kpi_kg || ""} onChange={upd("kpi_kg")} placeholder="86420" testid="brand-kpi-kg" />
          <Input label="KPI · CO₂ tonnes" v={b.kpi_co2 || ""} onChange={upd("kpi_co2")} placeholder="240" testid="brand-kpi-co2" />
          <Input label="KPI · customers" v={b.kpi_customers || ""} onChange={upd("kpi_customers")} placeholder="12402" testid="brand-kpi-customers" />
          <Input label="KPI · zones" v={b.kpi_zones || ""} onChange={upd("kpi_zones")} placeholder="38" testid="brand-kpi-zones" />
        </div>
      </Group>

      <Group title="Homepage · Referrals & Rewards">
        <Input label="Title" v={b.section_f_title || ""} onChange={upd("section_f_title")} testid="brand-section-f-title" />
        <Textarea label="Body" v={b.section_f_body || ""} onChange={upd("section_f_body")} testid="brand-section-f-body" />
        <div className="border-t border-[#F0ECE3] pt-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-medium mb-3">Customer card</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Input label="Kicker" v={b.referral_customer_kicker || ""} onChange={upd("referral_customer_kicker")} testid="brand-ref-cust-kicker" />
            <Input label="Title" v={b.referral_customer_title || ""} onChange={upd("referral_customer_title")} testid="brand-ref-cust-title" />
          </div>
          <Textarea label="Body" v={b.referral_customer_body || ""} onChange={upd("referral_customer_body")} testid="brand-ref-cust-body" />
          <Input label="CTA label" v={b.referral_customer_cta || ""} onChange={upd("referral_customer_cta")} testid="brand-ref-cust-cta" />
        </div>
        <div className="border-t border-[#F0ECE3] pt-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-medium mb-3">Partner card</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Input label="Kicker" v={b.referral_partner_kicker || ""} onChange={upd("referral_partner_kicker")} testid="brand-ref-part-kicker" />
            <Input label="Title" v={b.referral_partner_title || ""} onChange={upd("referral_partner_title")} testid="brand-ref-part-title" />
          </div>
          <Textarea label="Body" v={b.referral_partner_body || ""} onChange={upd("referral_partner_body")} testid="brand-ref-part-body" />
          <Input label="CTA label" v={b.referral_partner_cta || ""} onChange={upd("referral_partner_cta")} testid="brand-ref-part-cta" />
        </div>
      </Group>

      <Group title="Homepage · Product store">
        <Input label="Section title" v={b.section_g_title || ""} onChange={upd("section_g_title")} testid="brand-section-g-title" />
        <ItemsEditor
          label="Store items"
          value={b.store_items || []}
          fields={["name", "price", "tag"]}
          onChange={(v) => setB((s) => ({ ...s, store_items: v }))}
          testid="brand-store-items"
        />
      </Group>

      <Group title="Homepage · Testimonials">
        <Input label="Section title" v={b.section_h_title || ""} onChange={upd("section_h_title")} testid="brand-section-h-title" />
        <ItemsEditor
          label="Quotes"
          value={b.testimonials || []}
          fields={["who", "role", "q", "color"]}
          onChange={(v) => setB((s) => ({ ...s, testimonials: v }))}
          testid="brand-testimonials"
          hint="color: green | red"
        />
      </Group>

      <Group title="Homepage · Lead capture">
        <Input label="Title" v={b.section_i_title || ""} onChange={upd("section_i_title")} testid="brand-section-i-title" />
        <Textarea label="Body" v={b.section_i_body || ""} onChange={upd("section_i_body")} testid="brand-section-i-body" />
      </Group>

      {/* ----------- Government portal ----------- */}
      <Group title="Government portal · Hero">
        <Input label="Hero title" v={b.gov_hero_title || ""} onChange={upd("gov_hero_title")} testid="brand-gov-hero-title" />
        <Textarea label="Hero body" v={b.gov_hero_body || ""} onChange={upd("gov_hero_body")} testid="brand-gov-hero-body" />
      </Group>

      <Group title="Government portal · Map">
        <Input label="Map title" v={b.gov_map_title || ""} onChange={upd("gov_map_title")} testid="brand-gov-map-title" />
        <Textarea label="Map body" v={b.gov_map_body || ""} onChange={upd("gov_map_body")} testid="brand-gov-map-body" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input label="Jurisdictions" v={b.gov_jurisdictions || ""} onChange={upd("gov_jurisdictions")} placeholder="47" testid="brand-gov-jurisdictions" />
          <Input label="Zones live" v={b.gov_zones || ""} onChange={upd("gov_zones")} placeholder="320+" testid="brand-gov-zones" />
          <Input label="Citizens" v={b.gov_citizens || ""} onChange={upd("gov_citizens")} placeholder="1.2M+" testid="brand-gov-citizens" />
          <Input label="BMW compliance" v={b.gov_compliance || ""} onChange={upd("gov_compliance")} placeholder="100%" testid="brand-gov-compliance" />
        </div>
      </Group>

      <Group title="Government portal · Features (3 cards)">
        <Input label="Section title" v={b.gov_features_title || ""} onChange={upd("gov_features_title")} testid="brand-gov-features-title" />
        <Textarea label="Section body" v={b.gov_features_body || ""} onChange={upd("gov_features_body")} testid="brand-gov-features-body" />
        <div className="grid md:grid-cols-3 gap-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="space-y-2 p-3 bg-[#F7F6F2] rounded-md">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-medium">Card {n}</div>
              <Input label="Title" v={b[`gov_feat_${n}_title`] || ""} onChange={upd(`gov_feat_${n}_title`)} testid={`brand-gov-feat-${n}-title`} />
              <Textarea label="Body" v={b[`gov_feat_${n}_body`] || ""} onChange={upd(`gov_feat_${n}_body`)} testid={`brand-gov-feat-${n}-body`} />
            </div>
          ))}
        </div>
      </Group>

      <Group title="Government portal · Compliance">
        <Input label="Title" v={b.gov_compliance_title || ""} onChange={upd("gov_compliance_title")} testid="brand-gov-compliance-title" />
        <Textarea label="Body" v={b.gov_compliance_body || ""} onChange={upd("gov_compliance_body")} testid="brand-gov-compliance-body" />
        <ListEditor label="Bullets (one per line)" value={b.gov_compliance_bullets || []} onChange={(v) => setB((s) => ({ ...s, gov_compliance_bullets: v }))} testid="brand-gov-compliance-bullets" />
      </Group>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4">
        <div className="font-heading mb-2">Public domain</div>
        <p className="text-xs text-[#5C5A55] -mt-2">
          Override the e-bill share URLs (and other public links) with your custom production domain.
          Leave empty to use the live request host. Format: <code>https://yourdomain.com</code> (no trailing slash).
        </p>
        <Input
          label="E-bill custom domain (public_app_url)"
          v={b.public_app_url || ""}
          onChange={upd("public_app_url")}
          testid="brand-public-app-url"
          placeholder="https://rescigo.in"
        />
        {b.public_app_url && (
          <div className="space-y-1.5">
            <div className="text-[11px] text-[#2D5A27] font-mono">
              E-bills will be shared as: {b.public_app_url.replace(/\/$/, "")}/ebill/&lt;invoice_no&gt;
            </div>
            <div className="text-[11px] text-[#B12A1E] bg-[#FEF1EE] border border-[#FBD9D2] px-3 py-2 rounded">
              <b>Heads-up:</b> only set this if your custom domain (<code className="font-mono">{b.public_app_url.replace(/^https?:\/\//, "")}</code>) is serving <i>this same backend</i>. Otherwise the link will say "Invoice not found" because the deployed site won't have the invoice in its database. Leave blank to use the current app URL automatically — works everywhere.
            </div>
            <button type="button" onClick={() => upd("public_app_url")("")}
                    data-testid="brand-clear-public-app-url"
                    className="text-[11px] text-[#5C5A55] underline hover:text-[#1A1A1A]">
              Clear custom domain (use auto)
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4">
        <div className="font-heading mb-2">Contact</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Address" v={b.company_address || ""} onChange={upd("company_address")} testid="brand-address" />
          <Input label="Support email" type="email" v={b.support_email || ""} onChange={upd("support_email")} testid="brand-support-email" />
          <Input label="Support phone" v={b.support_phone || ""} onChange={upd("support_phone")} testid="brand-support-phone" />
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          {["linkedin", "instagram", "twitter", "youtube"].map((s) => (
            <Input key={s} label={s} v={(b.social || {})[s] || ""} onChange={updSocial(s)} testid={`brand-social-${s}`} />
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="brand-save"
                className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
          {busy ? "Saving…" : "Save branding"}
        </button>
      </div>
    </div>
  );
}

function LogoSlot({ label, url, onChange, bg, small, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2 font-medium">{label}</label>
      <div className="rounded-md p-5 flex items-center justify-center mb-2 border border-[#E5E2D9]" style={{ background: bg, height: small ? 80 : 120 }}>
        {url ? (
          <img src={url} alt={label} style={{ maxHeight: small ? 48 : 72, maxWidth: "100%", objectFit: "contain" }}
               onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <span className="text-xs text-[#8A8780]">No image</span>
        )}
      </div>
      <input value={url || ""} onChange={onChange} data-testid={testid} placeholder="/brand/your-logo.png or https://…"
             className="w-full h-9 px-3 border border-[#E5E2D9] rounded-md text-xs font-mono bg-white" />
    </div>
  );
}

function Input({ label, v, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5 font-medium">{label}</label>
      <input type={type} value={v} onChange={onChange} placeholder={placeholder} data-testid={testid}
             className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white" />
    </div>
  );
}



function Textarea({ label, v, onChange, placeholder, testid, rows = 3 }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5 font-medium">{label}</label>
      <textarea value={v} onChange={onChange} placeholder={placeholder} rows={rows} data-testid={testid}
                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm bg-white resize-y" />
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4">
      <div className="font-heading">{title}</div>
      {children}
    </div>
  );
}

function ListEditor({ label, value, onChange, testid }) {
  const [text, setText] = useState((value || []).join("\n"));
  // Sync if upstream value changes (e.g. fresh load)
  useEffect(() => { setText((value || []).join("\n")); /* eslint-disable-line */ }, [value?.length]);
  const commit = () => {
    const lines = text.split("\n").map((s) => s.trim()).filter(Boolean);
    onChange(lines);
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5 font-medium">{label}</label>
      <textarea value={text} onChange={(e) => setText(e.target.value)} onBlur={commit} rows={5} data-testid={testid}
                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm bg-white font-mono resize-y" />
      <div className="text-[10px] text-[#8A8780] mt-1">Tab/blur to save · {text.split("\n").filter(s => s.trim()).length} item(s)</div>
    </div>
  );
}

function ItemsEditor({ label, value, fields, onChange, testid, hint }) {
  const items = value || [];
  const updItem = (idx, key, val) => {
    const next = items.map((it, i) => i === idx ? { ...it, [key]: val } : it);
    onChange(next);
  };
  const addItem = () => {
    const blank = {};
    fields.forEach((f) => (blank[f] = ""));
    onChange([...items, blank]);
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-medium">{label}</label>
        <button onClick={addItem} className="text-xs text-[#2D5A27] inline-flex items-center gap-1 hover:underline">
          <Plus size={11} weight="bold" /> Add
        </button>
      </div>
      {hint && <div className="text-[10px] text-[#8A8780] mb-2">{hint}</div>}
      <div className="space-y-2">
        {items.length === 0 && <div className="text-[11px] text-[#8A8780] py-2">No items · "Add" to create one (defaults will be used)</div>}
        {items.map((it, idx) => (
          <div key={idx} className="grid gap-2 p-3 bg-[#F7F6F2] rounded-md relative" style={{ gridTemplateColumns: `repeat(${fields.length}, 1fr) auto` }}>
            {fields.map((f) => (
              <input key={f} value={it[f] ?? ""}
                     onChange={(e) => updItem(idx, f, e.target.value)}
                     placeholder={f}
                     className="h-9 px-2 border border-[#E5E2D9] rounded text-xs bg-white" />
            ))}
            <button onClick={() => removeItem(idx)} className="p-1.5 text-[#B91C1C] hover:bg-white rounded">
              <Trash size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
